import { useState, useCallback } from "react";
import { Users, Clock, ShoppingCart, MoreVertical, Edit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { RestaurantTable } from "@/hooks/useTables";

interface TableMapViewProps {
  tables: RestaurantTable[];
  onSelectTable: (table: RestaurantTable) => void;
  onEditTable: (table: RestaurantTable) => void;
}

const statusConfig = {
  available: {
    bg: "bg-emerald-500",
    border: "border-emerald-600",
    shadow: "shadow-emerald-500/30",
    text: "text-white",
    label: "Disponível",
    glow: "hover:shadow-emerald-500/50",
  },
  occupied: {
    bg: "bg-amber-500",
    border: "border-amber-600",
    shadow: "shadow-amber-500/30",
    text: "text-white",
    label: "Ocupada",
    glow: "hover:shadow-amber-500/50",
  },
  reserved: {
    bg: "bg-blue-500",
    border: "border-blue-600",
    shadow: "shadow-blue-500/30",
    text: "text-white",
    label: "Reservada",
    glow: "hover:shadow-blue-500/50",
  },
};

// Visual table shapes
type TableShape = "square" | "round" | "rectangle";

function getTableShape(capacity: number | null): TableShape {
  if (!capacity) return "square";
  if (capacity <= 2) return "round";
  if (capacity <= 4) return "square";
  return "rectangle";
}

function TableVisual({ 
  table, 
  onSelect, 
  onEdit 
}: { 
  table: RestaurantTable; 
  onSelect: () => void; 
  onEdit: () => void;
}) {
  const config = statusConfig[table.status];
  const shape = getTableShape(table.capacity);

  const baseClasses = cn(
    "relative flex flex-col items-center justify-center cursor-pointer",
    "transition-all duration-300 ease-out",
    "border-2 shadow-lg",
    config.bg,
    config.border,
    config.shadow,
    config.glow,
    "hover:scale-105 hover:shadow-xl",
    "active:scale-95"
  );

  const shapeClasses = cn(
    shape === "round" && "rounded-full w-20 h-20",
    shape === "square" && "rounded-xl w-24 h-24",
    shape === "rectangle" && "rounded-xl w-32 h-20"
  );

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(baseClasses, shapeClasses)}
            onClick={onSelect}
          >
            {/* Table Number */}
            <span className={cn("text-2xl font-bold", config.text)}>
              {table.number}
            </span>
            
            {/* Table Name/Nickname */}
            {table.name && (
              <span className={cn("text-[10px] font-medium truncate max-w-full px-1", config.text, "opacity-90")}>
                {table.name}
              </span>
            )}
            
            {/* Capacity indicator */}
            <div className={cn("flex items-center gap-0.5 text-xs", config.text, "opacity-80", table.name ? "mt-0.5" : "mt-1")}>
              <Users className="h-3 w-3" />
              <span>{table.capacity}</span>
            </div>

            {/* Status pulse animation for occupied tables */}
            {table.status === "occupied" && (
              <div className="absolute -top-1 -right-1">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-300"></span>
                </span>
              </div>
            )}

            {/* Reserved indicator */}
            {table.status === "reserved" && (
              <div className="absolute -top-1 -right-1">
                <Clock className="h-4 w-4 text-white bg-blue-600 rounded-full p-0.5" />
              </div>
            )}

            {/* Context Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "absolute top-0.5 left-0.5 h-6 w-6 opacity-0 group-hover:opacity-100",
                    config.text,
                    "hover:bg-white/20"
                  )}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(); }}>
                  <Edit className="h-4 w-4 mr-2" />
                  Editar Mesa
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-sm">
          <div className="space-y-1">
            <p className="font-semibold">Mesa {table.number}</p>
            {table.name && <p className="text-muted-foreground">{table.name}</p>}
            <div className="flex items-center gap-2">
              <Badge className={cn("text-xs", config.bg, "text-white")}>
                {config.label}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {table.capacity} lugares
              </span>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function TableMapView({ tables, onSelectTable, onEditTable }: TableMapViewProps) {
  const activeTables = tables.filter(t => t.is_active);

  // Group tables by status for the legend
  const statusCounts = {
    available: activeTables.filter(t => t.status === "available").length,
    occupied: activeTables.filter(t => t.status === "occupied").length,
    reserved: activeTables.filter(t => t.status === "reserved").length,
  };

  // Sort: occupied first, then reserved, then available
  const sortedTables = [...activeTables].sort((a, b) => {
    const order = { occupied: 0, reserved: 1, available: 2 };
    return order[a.status] - order[b.status];
  });

  return (
    <div className="space-y-6">
      {/* Legend */}
      <div className="flex items-center justify-center gap-6 p-4 bg-muted/50 rounded-lg">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-emerald-500 shadow-md"></div>
          <span className="text-sm">Disponível ({statusCounts.available})</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-amber-500 shadow-md relative">
            <span className="absolute inset-0 rounded-full bg-amber-400 animate-ping opacity-50"></span>
          </div>
          <span className="text-sm">Ocupada ({statusCounts.occupied})</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-blue-500 shadow-md"></div>
          <span className="text-sm">Reservada ({statusCounts.reserved})</span>
        </div>
      </div>

      {/* Table Map Grid */}
      <div className="relative min-h-[400px] bg-gradient-to-br from-background to-muted/30 rounded-xl border-2 border-dashed border-muted p-8">
        {/* Floor grid pattern */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `
              linear-gradient(to right, currentColor 1px, transparent 1px),
              linear-gradient(to bottom, currentColor 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px'
          }}
        />

        {/* Tables Grid */}
        <div className="relative grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-6 justify-items-center">
          {sortedTables.map((table) => (
            <div key={table.id} className="group">
              <TableVisual
                table={table}
                onSelect={() => onSelectTable(table)}
                onEdit={() => onEditTable(table)}
              />
            </div>
          ))}
        </div>

        {/* Empty state */}
        {activeTables.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <ShoppingCart className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">Nenhuma mesa cadastrada</p>
              <p className="text-sm">Cadastre mesas para visualizá-las no mapa</p>
            </div>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-4 text-center border border-emerald-200 dark:border-emerald-800">
          <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
            {statusCounts.available}
          </p>
          <p className="text-sm text-emerald-700 dark:text-emerald-300">Mesas Disponíveis</p>
        </div>
        <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg p-4 text-center border border-amber-200 dark:border-amber-800">
          <p className="text-3xl font-bold text-amber-600 dark:text-amber-400">
            {statusCounts.occupied}
          </p>
          <p className="text-sm text-amber-700 dark:text-amber-300">Mesas Ocupadas</p>
        </div>
        <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-4 text-center border border-blue-200 dark:border-blue-800">
          <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
            {statusCounts.reserved}
          </p>
          <p className="text-sm text-blue-700 dark:text-blue-300">Mesas Reservadas</p>
        </div>
      </div>
    </div>
  );
}
